/**
 * Light, dark, or whatever the machine says.
 *
 * The tenant stylesheet already carries a full dark palette and has since it was written — every
 * publish validates both modes for contrast. What it never had was a way for anybody to *reach*
 * dark: nothing set the class, so the only users who ever saw it were the ones whose operating
 * system was already dark. This module is the missing half.
 *
 * **Three states, not two.** `system` is a real choice and it is the default, so a machine that
 * switches at sunset switches the app with it. `light` and `dark` are the user overriding that,
 * and an override has to survive a reload or it is not one.
 *
 * The class vocabulary is fixed by `packages/theme/src/css.ts`, which emits:
 *
 *     :root { …light… }
 *     .dark { …dark… }
 *     @media (prefers-color-scheme: dark) { :root:not(.dark):not(.light) { …dark… } }
 *
 * So: `dark` adds `.dark`, `light` adds `.light`, `system` adds neither. The `.light` class does
 * nothing except stop the media query from firing — which is the whole reason it exists, and the
 * reason a `:root:not(.dark)` guard on its own was wrong.
 */

export type ColorScheme = 'light' | 'dark' | 'system'

/**
 * Namespaced, because these apps are served from the same origin as the API in production and a
 * bare `theme` would be one collision away from something else reading it.
 */
export const COLOR_SCHEME_STORAGE_KEY = 'ems.color-scheme'

const CLASS: Record<ColorScheme, string | null> = {
  dark: 'dark',
  light: 'light',
  system: null,
}

/** Narrow whatever came out of storage. Anything unrecognized is `system`, never a throw. */
export function parseColorScheme(value: unknown): ColorScheme {
  return value === 'dark' || value === 'light' ? value : 'system'
}

/**
 * Just the part of an element this needs.
 *
 * `Element` would be the obvious type and is the wrong one: it drags in the whole DOM for two
 * method calls, so a test has to either stand up a document or assert a hand-made object into a
 * shape it plainly is not. Naming the structural requirement makes `document.documentElement` and a
 * four-line double equally valid, which is what "testable without a DOM" has to mean to be true.
 */
export type ClassListTarget = { classList: Pick<DOMTokenList, 'add' | 'remove'> }

/**
 * Put the choice on the element, and take the other one off.
 *
 * Both classes are removed before either is added, because the states are exclusive and a root
 * carrying `.dark .light` at once is a root where the tenant stylesheet's selectors disagree with
 * each other.
 */
export function applyColorScheme(scheme: ColorScheme, root: ClassListTarget): void {
  root.classList.remove('dark', 'light')
  const next = CLASS[scheme]
  if (next !== null) root.classList.add(next)
}

/**
 * Read the stored choice.
 *
 * `storage` is a parameter rather than a reach for `globalThis.localStorage` so this is testable
 * without a DOM — and so the throw is handled in one place. Safari in private browsing throws on
 * `getItem`, and a theme preference is not worth taking the app down for.
 */
export function readColorScheme(storage: Pick<Storage, 'getItem'>): ColorScheme {
  try {
    return parseColorScheme(storage.getItem(COLOR_SCHEME_STORAGE_KEY))
  } catch {
    return 'system'
  }
}

/** Persist the choice, or forget it when the user goes back to following the machine. */
export function storeColorScheme(
  scheme: ColorScheme,
  storage: Pick<Storage, 'setItem' | 'removeItem'>,
): void {
  try {
    if (scheme === 'system') storage.removeItem(COLOR_SCHEME_STORAGE_KEY)
    else storage.setItem(COLOR_SCHEME_STORAGE_KEY, scheme)
  } catch {
    // A preference that cannot be saved is a preference that lasts one session. That is a much
    // smaller problem than a toggle that throws.
  }
}

/**
 * The script that runs **before first paint**, injected into every app's `<head>`.
 *
 * This has to be inline and synchronous, and it has to be here rather than in a React effect, for
 * exactly the reason the tenant theme is a `<link>` rather than a `fetch`: by the time an effect
 * runs the page has already painted, so a user who chose dark sees a white flash on every cold
 * load. A module script is deferred; an external script is a round trip. Neither is early enough.
 *
 * It duplicates `applyColorScheme` in plain ES5, because a string of source cannot import. That
 * duplication is the risk, so `color-scheme.test.ts` runs this script against a fake document and
 * asserts it agrees with `applyColorScheme` for all three states — if the two ever drift, the
 * failure is a test rather than a flash somebody has to reproduce.
 *
 * **If a Content-Security-Policy is ever added, this needs a nonce or a hash.** There is none
 * today. A CSP with `script-src 'self'` would silently drop this and return the flash, with no
 * error anywhere except the console.
 */
export const COLOR_SCHEME_BOOT_SCRIPT =
  `try{var s=localStorage.getItem(${JSON.stringify(COLOR_SCHEME_STORAGE_KEY)});` +
  `if(s==="dark"||s==="light")document.documentElement.classList.add(s)}catch(e){}`
